import {
    GetTeamQuery,
    GetTeamQueryVariables,
    ListTeamsByOrganizationIdQuery,
    ListTeamsByOrganizationIdQueryVariables,
    Team
} from "../../../API";
import {
    getTeam,
    listTeamsByOrganizationId
} from "../../../graphql/queries";

import { API } from "aws-amplify";
import { AuthIDTokenSupplierFactory } from "../../auth/AuthIDTokenSupplierFactory";
import ClientLogger from "../../logging/ClientLogger";
import ClientLoggerFactory from "../../logging/ClientLoggerFactory";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { IDTokenSupplier } from "../../auth/IDTokenSupplier";
import { TeamDAO } from "./TeamDAO";
import { graphqlOperation } from "aws-amplify";

export class GraphQLTeamDAO implements TeamDAO {
    public static readonly GET_BY_ID_ATTEMPT = "GraphQLTeamDAO.getById.Attempt";
    public static readonly GET_BY_ID_FAILURE = "GraphQLTeamDAO.getById.Failure";
    public static readonly LIST_BY_ORGANIZATION_ID_ATTEMPT = "GraphQLTeamDAO.getTeamsByOrganizationId.Attempt";
    public static readonly LIST_BY_ORGANIZATION_ID_FAILURE = "GraphQLTeamDAO.getTeamsByOrganizationId.Failure";

    private readonly api: typeof API;
    private readonly gqlOperation: typeof graphqlOperation;
    private readonly logger: ClientLogger;
    private readonly idTokenSupplier: IDTokenSupplier;

    constructor(
        api: typeof API,
        gqlOperation: typeof graphqlOperation,
        logger: ClientLogger,
        idTokenSupplier: IDTokenSupplier
    ) {
        this.api = api;
        this.gqlOperation = gqlOperation;
        this.logger = logger;
        this.idTokenSupplier = idTokenSupplier;
    }

    public async getById(id: string): Promise<Team | null> {
        try {
            this.logger.info(`Getting team by id ${id}`, undefined, [GraphQLTeamDAO.GET_BY_ID_ATTEMPT]);

            const variables: GetTeamQueryVariables = {
                id: id
            };
            const authToken: string = await this.idTokenSupplier.get();
            const gqlOperation = this.gqlOperation(
                getTeam,
                variables,
                authToken
            );
            const queryResult = await this.api.graphql(gqlOperation) as GraphQLResult<GetTeamQuery>;
            const team = queryResult.data?.getTeam;
            if (!team) {
                return null;
            }
            return team;
        } catch (error) {
            this.logger.error(`Error getting team by id ${id}`, error, [GraphQLTeamDAO.GET_BY_ID_FAILURE]);
            throw error;
        }
    }

    async listByOrganizationId(organizationId: string): Promise<Team[]> {
        try {
            this.logger.info(`Getting teams for organization ${organizationId}`, undefined, [GraphQLTeamDAO.LIST_BY_ORGANIZATION_ID_ATTEMPT]);

            const variables: ListTeamsByOrganizationIdQueryVariables = {
                organizationId: organizationId
            };
            const authToken: string = await this.idTokenSupplier.get();
            const gqlOperation = this.gqlOperation(
                listTeamsByOrganizationId,
                variables,
                authToken
            );
            const queryResult = await this.api.graphql(gqlOperation) as GraphQLResult<ListTeamsByOrganizationIdQuery>;
            const items = queryResult.data?.listTeamsByOrganizationId?.items ?? [];
            return items.filter((item): item is Team => item !== null);
        } catch (error) {
            this.logger.error(`Error getting teams for organization ${organizationId}`, error, [GraphQLTeamDAO.LIST_BY_ORGANIZATION_ID_FAILURE]);
            throw error;
        }
    }
}

export const graphQLTeamDAO = new GraphQLTeamDAO(
    API,
    graphqlOperation,
    ClientLoggerFactory.getClientLogger("GraphQLTeamDAO"),
    AuthIDTokenSupplierFactory.getInstance()
);